import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "ecotour.theme.preference";
export const DARK_THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

const ThemeContext = createContext(null);

const readStoredTheme = () => {
  if (typeof window === "undefined") return "";
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "";
  } catch (_error) {
    return "";
  }
};

const readSystemTheme = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia(DARK_THEME_MEDIA_QUERY).matches ? "dark" : "light";
};

const applyTheme = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  themeMeta?.setAttribute("content", theme === "dark" ? "#0e1512" : "#f7f8f4");
};

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(readStoredTheme);
  const [systemTheme, setSystemTheme] = useState(readSystemTheme);
  const theme = preference || systemTheme;

  useEffect(() => {
    if (preference || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia(DARK_THEME_MEDIA_QUERY);
    const handleSystemThemeChange = (event) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    handleSystemThemeChange(mediaQuery);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
      return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
    }

    mediaQuery.addListener?.(handleSystemThemeChange);
    return () => mediaQuery.removeListener?.(handleSystemThemeChange);
  }, [preference]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setPreference((currentPreference) => {
      const currentTheme = currentPreference || systemTheme;
      const nextTheme = currentTheme === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch (_error) {
        // The selected theme still applies for this session when storage is unavailable.
      }
      return nextTheme;
    });
  }, [systemTheme]);

  const value = useMemo(() => ({ theme, preference, toggleTheme }), [preference, theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
